import { DestroyRef, Injectable, signal } from '@angular/core';
import { Location } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { interval, filter } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SwProcessingService } from './sw-processing.service';

/**
 * Longest we wait for the service worker to confirm activation before replacing
 * the page anyway. See activateUpdate().
 */
export const ACTIVATION_TIMEOUT_MS = 3000;

/** Shape of the parts of ngsw.json this service reads. */
interface NgswManifest {
  timestamp?: unknown;
}

/** Console handle installed as `window.__rwUpdate` outside production. */
interface UpdateDevHook {
  status: () => {
    serviceWorkerEnabled: boolean;
    pollIntervalMs: number;
    lastSeenNgswTimestamp?: number;
    announcedVersion?: number | string;
  };
  simulate: () => string;
}

/**
 * Detects a new deploy and applies it.
 *
 * Two detectors run side by side: the service worker's own VERSION_READY event,
 * and a cache-busted poll of ngsw.json whose build timestamp changes the moment a
 * release lands. Either one announces the new version, once per version.
 *
 * The update is applied by UpdateStripComponent ("Refresh now") or by
 * SwUpdateNavigationService on the next page navigation. Neither replaces the
 * page while an API call or contract transaction is running.
 */
@Injectable({
  providedIn: 'root'
})
export class SwUpdateService {

  // Signal instead of BehaviorSubject
  private readonly _updateAvailable = signal<boolean>(false);

  // Expose as readonly signal (like observable$)
  readonly updateAvailable = this._updateAvailable.asReadonly();

  private lastSeenNgswTimestamp?: number;
  /** Version already announced, so the two detectors announce one deploy once. */
  private announcedVersion?: number | string;

  constructor(
    private swUpdate: SwUpdate,
    private swProcessingService: SwProcessingService,
    private location: Location,
    private destroyRef: DestroyRef
  ) {
    // Installed before the check below, so it also works under `ng serve`, where the
    // service worker is off.
    this.installDevHook();

    if (!swUpdate.isEnabled) {
      return;
    }

    this.checkForUpdates();
    void this.pollNgswManifest();

    interval(this.pollIntervalMs)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.checkForUpdates();
        void this.pollNgswManifest();
      });

    this.swUpdate.versionUpdates
      .pipe(
        filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => this.announce(event.latestVersion.hash));
  }

  /**
   * Outside production only: a console handle for checking the update flow
   * locally, where there is no real deploy to detect.
   *
   *   __rwUpdate.status()    → service worker state and the last detected version
   *   __rwUpdate.simulate()  → shows the "Refresh now" strip; needs no service worker
   *
   * Never installed in a production build.
   *
   * @returns Nothing.
   */
  private installDevHook(): void {
    if (environment.production || typeof window === 'undefined') {
      return;
    }
    const hook: UpdateDevHook = {
      status: () => ({
        serviceWorkerEnabled: this.swUpdate.isEnabled,
        pollIntervalMs: this.pollIntervalMs,
        lastSeenNgswTimestamp: this.lastSeenNgswTimestamp,
        announcedVersion: this.announcedVersion
      }),
      simulate: () => {
        this.announce(`manual-${Date.now()}`);
        return 'Update announced — the "Refresh now" strip should be visible now.';
      }
    };
    (window as unknown as { __rwUpdate?: UpdateDevHook }).__rwUpdate = hook;
  }

  /**
   * How often to check for a new deploy: every 2 minutes on production to keep
   * request volume down, every 30s elsewhere so a dev/staging deploy shows up
   * while you are still looking at it.
   *
   * @returns The poll interval in milliseconds.
   */
  private get pollIntervalMs(): number {
    return environment.production ? 2 * 60 * 1000 : 30 * 1000;
  }

  /**
   * Asks the service worker to look for a new version. A failed check is ignored;
   * the next interval retries.
   *
   * @returns Nothing.
   */
  private checkForUpdates(): void {
    this.swUpdate.checkForUpdate().catch(() => undefined);
  }

  /**
   * Detects a deploy from the ngsw.json build timestamp, which works even when
   * cache headers keep the service worker on the old version for a while.
   *
   * @returns A promise that settles after one poll.
   */
  private async pollNgswManifest(): Promise<void> {
    try {
      const response = await fetch(`ngsw.json?cb=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) { return; }
      const manifest: NgswManifest = await response.json();
      const timestamp = manifest?.timestamp;
      if (typeof timestamp !== 'number') { return; }
      if (this.lastSeenNgswTimestamp === undefined) {
        this.lastSeenNgswTimestamp = timestamp;
        return;
      }
      if (timestamp !== this.lastSeenNgswTimestamp) {
        this.lastSeenNgswTimestamp = timestamp;
        this.announce(timestamp);
      }
    } catch {
      // Network hiccup or a non-JSON body: the next interval retries.
    }
  }

  /**
   * Marks an update as available, at most once per detected version. Both
   * detectors fire for the same deploy, so without this guard one deploy would
   * be announced twice.
   *
   * @param version - The new build's hash or ngsw.json timestamp.
   * @returns Nothing.
   */
  private announce(version: number | string): void {
    if (this.announcedVersion === version) {
      return;
    }
    this.announcedVersion = version;
    this._updateAvailable.set(true);
  }

  /**
   * Swaps the service worker over to the new version without replacing the page.
   *
   * Capped at ACTIVATION_TIMEOUT_MS on purpose: swUpdate.activateUpdate() waits
   * for the worker to confirm, and that promise never settles when the worker has
   * not finished preparing the new version (the ngsw.json poll can spot a deploy
   * before then). A hang is not a rejection, so without the cap "Refresh now"
   * would sit on "Refreshing…" forever. Activation is an optimisation; a full
   * page load picks up the new version regardless.
   *
   * @returns A promise that settles once the worker confirms or the cap passes.
   */
  async activateUpdate(): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        this.swUpdate.activateUpdate(),
        new Promise<void>((resolve) => { timer = setTimeout(resolve, ACTIVATION_TIMEOUT_MS); })
      ]);
    } catch {
      // Activation failed: replace the page anyway, the reload loads the new build.
    } finally {
      if (timer !== undefined) { clearTimeout(timer); }
    }
  }

  /**
   * "Refresh now": waits until no API call or contract transaction is running,
   * activates the new version and reloads the page.
   *
   * @returns A promise that settles as the page starts reloading.
   */
  async activateUpdateAndReload(): Promise<void> {
    await this.swProcessingService.whenIdle();
    await this.activateUpdate();
    this.reloadWindow();
  }

  /**
   * Applies the update as part of a navigation the user just started: loads
   * `url` as a full page so the new screen comes from the new build.
   *
   * @param url - The router URL being navigated to.
   * @returns A promise that settles as the page starts loading.
   */
  async activateAndNavigate(url: string): Promise<void> {
    await this.activateUpdate();
    this.navigateWindow(url);
  }

  /**
   * Reloads the current page. One of the only two places the update flow
   * replaces the document; specs spy on it.
   *
   * @returns Nothing.
   */
  reloadWindow(): void {
    window.location.reload();
  }

  /**
   * Loads `url` as a full page: the address is set first and then reloaded.
   * The router has usually committed `url` by now; the history entry is only
   * added when it has not, so Back is not doubled.
   *
   * @param url - The router URL to load.
   * @returns Nothing.
   */
  navigateWindow(url: string): void {
    if (this.location.path(true) !== url) {
      window.history.pushState(null, '', this.location.prepareExternalUrl(url));
    }
    this.reloadWindow();
  }
}
